import clsx from 'clsx'
import { useCallback, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import useToggle from 'hooks/useToggle'
import Clickable from 'components/Common/Clickable'
import Expander from 'components/utils/Expander'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from '../../../../LuxKit/TextLink'
import noop from 'lib/function/noop'
import LoadingAnimation from '../Loading/LoadingAnimation'
import Group from 'components/utils/Group'
import { useIntl } from 'react-intl'

const Wrapper = styled.div`
  &.border {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.is-open {
    padding-bottom: ${rem(20)};
    transition: padding-bottom .2s ease;
  }
`

const FilterGroup = styled(Clickable)`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: ${rem(8)} 0 ${rem(8)};
`

const FadingReset = styled(TextLink)`
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;

  &.visible {
    pointer-events: auto;
    opacity: 1;
  }
`

interface Props {
  title: React.ReactNode
  children: React.ReactNode
  canReset?: boolean
  onReset?: (e: React.MouseEvent<HTMLButtonElement>) => void
  resetLabel?: string
  showBorder?: boolean
  showDropdown?: boolean
  allowToggle?: boolean
  loading?: boolean
  opened?: boolean
  'data-testid'?: string
}

function FilterPanelFilterGroup(props: Props) {
  const { children, title, onReset = noop, canReset, resetLabel, showBorder = true, showDropdown = true, allowToggle = true, loading = false, opened = true, 'data-testid': dataTestId } = props
  const intl = useIntl()
  const [open, toggle] = useToggle(opened)
  const resetRef = useRef(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const onGroupClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.target === resetRef.current) {
      contentRef.current?.querySelectorAll('input').forEach(input => {
        if (input.type === 'checkbox') {
          input.checked = false
        } else if (input.type === 'range') {
          if (input.name.includes('start')) {
            input.value = input.min
          } else {
            input.value = input.max
          }
        } else {
          input.value = ''
        }
      })
      onReset(e)
    } else if (allowToggle) {
      toggle()
    }
  }, [allowToggle, onReset, toggle])

  return (
    <Wrapper ref={contentRef} className={clsx({ 'is-open': open, border: showBorder })} data-testid={dataTestId}>
      <FilterGroup onClick={onGroupClick}>
        <Group direction="horizontal" verticalAlign="center" gap={8}>
          {typeof title === 'string' && <Heading variant="heading6">{title}</Heading>}
          {typeof title !== 'string' && title}
          {loading && <LoadingAnimation size={8} />}
        </Group>
        <Group direction="horizontal" verticalAlign="center" gap={8}>
          <FadingReset
            aria-hidden={!canReset}
            ref={resetRef}
            className={clsx({ visible: canReset })}
            size="small"
          >
            {resetLabel ?? intl.formatMessage({
              id: '1BCuuKA_YbmI7wo_U3rn',
              defaultMessage: 'Reset',
              description: 'Link to reset filter selections in the search or listing filters panel',
            })}
          </FadingReset>
          {showDropdown && <DropdownChevron open={open} />}
        </Group>
      </FilterGroup>
      <Expander expanded={open}>
        {children}
      </Expander>
    </Wrapper>
  )
}

export default React.memo(FilterPanelFilterGroup)
